import React from "react";
import { Button, toast, Stack } from "@altimateai/lego";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createWarehouseSchedulerEvent,
  createWarehouseSchedulerEventForDynamicResizing,
  editWarehouseSchedulerEvent,
} from "@/helpers/apis";
import { GET_WAREHOUSE_SCHEDULED_EVENTS } from "../../constants";
import { CommonScheduleForm } from "./CommonScheduleForm";
import {
  addOrUpdateFormSubmission,
  FormValues,
  initialValues as defaultInitialValues,
} from "./formConfig";
import { ScrollContainer } from "@/components/scrollContainer";
import styles from "./styles.module.scss";
import { useRef } from "react";
import { FormikProps } from "formik";
import { LoadingButton } from "@altimateai/lego";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import { CloseIcon } from "@/assets/icons";
import { ScheduleCreateRequest, ScheduleUpdateRequest } from "../../types";
import useWarehouseScheduler from "../scheduler/useWarehouseScheduler";
import { WarehouseTracking } from "@/lib/tracking/warehouse";

dayjs.extend(timezone);

interface ScheduleFormProps {
  instanceId: string;
  warehouseName: string;
  warehouseSize?: string;
  defaultWarehouseSize?: string;
  handleClose: () => void;
  selectedDateTime?: string;
  selectedEndDateTime?: string;
  isEdit?: boolean;
  scheduleId?: string;
  initialValues?: FormValues;
  handleCancel?: () => void;
  isDynamic?: boolean;
}

const AddScheduleForm: React.FC<ScheduleFormProps> = ({
  warehouseName,
  instanceId,
  warehouseSize = "",
  defaultWarehouseSize = "",
  handleClose,
  selectedDateTime,
  selectedEndDateTime,
  isEdit = false,
  scheduleId = "",
  initialValues,
  handleCancel,
  isDynamic = false,
}) => {
  const queryClient = useQueryClient();
  const formikRef = useRef<FormikProps<FormValues> | null>(null);
  const { data: schedulerData } = useWarehouseScheduler({
    instance_id: instanceId,
    warehouse_name: warehouseName,
  });

  const createFn = isDynamic
    ? createWarehouseSchedulerEventForDynamicResizing
    : createWarehouseSchedulerEvent;

  const { mutate } = useMutation({
    mutationFn: (data: ScheduleCreateRequest | ScheduleUpdateRequest) => {
      if (isEdit) {
        return editWarehouseSchedulerEvent(scheduleId, data as ScheduleUpdateRequest);
      } else {
        return createFn(data as ScheduleCreateRequest);
      }
    },
    onSuccess: (_data, variables) => {
      if (isEdit) {
        WarehouseTracking.scheduleEdited(warehouseName, scheduleId);
      } else {
        WarehouseTracking.scheduleCreated(
          warehouseName,
          (variables as ScheduleCreateRequest).schedule_type ?? "manual"
        );
      }
      queryClient.invalidateQueries({
        queryKey: [GET_WAREHOUSE_SCHEDULED_EVENTS, instanceId, warehouseName],
      });
      handleClose();
    },
    onError: (error: unknown) => {
      toast({
        description:
          error instanceof Error ? error.message : String(error) || "Something went wrong",
      });
    },
  });

  const handleSubmit = (values: FormValues) => {
    try {
      addOrUpdateFormSubmission(
        warehouseName,
        instanceId,
        mutate,
        values,
        schedulerData,
        scheduleId
      );
    } catch (error) {
      toast({ description: (error as Error).message });
    }
  };

  const resolvedInitialValues =
    initialValues ||
    defaultInitialValues({
      warehouseFrom: warehouseSize,
      startDate: selectedDateTime,
      endDate: selectedEndDateTime || dayjs(selectedDateTime).add(1, "hour").toISOString(),
      warehouseTo: "",
    });

  return (
    <ScrollContainer>
      <div className={styles.formWrapper}>
        <div className={styles.formHeader}>
          <h4>{!isEdit ? "Add Custom Schedule" : "Edit Custom Schedule"}</h4>
          <div className={styles.headerRight}>
            <button className={styles.closeButton} onClick={isEdit ? handleCancel : handleClose}>
              <CloseIcon />
            </button>
          </div>
        </div>
        <div className={styles.formBody}>
          <CommonScheduleForm
            handleSubmit={handleSubmit}
            initialValues={resolvedInitialValues}
            warehouseSize={warehouseSize}
            defaultWarehouseSize={defaultWarehouseSize}
            formikRef={formikRef}
            isEdit={isEdit}
          >
            {({ values }) => (
              <div className="al-grid al-grid-cols-1">
                <Stack justify="end" gap="sm">
                  <Button variant="outline" onClick={isEdit ? handleCancel : handleClose}>
                    Cancel
                  </Button>
                  <LoadingButton
                    loading={formikRef.current?.isSubmitting || false}
                    type="submit"
                    color="primary"
                    disabled={!values.warehouseChangeTo && values.scheduleBlocker !== "Yes"}
                  >
                    {isEdit ? "Update Schedule" : "Create Schedule"}
                  </LoadingButton>
                </Stack>
              </div>
            )}
          </CommonScheduleForm>
        </div>
      </div>
    </ScrollContainer>
  );
};

export default AddScheduleForm;
